import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { CandidateProfile, EmployerProfile, RecruiterProfile, User } from '../types/core';
import type { Application, ApplicationStatus, CandidatePresentation, ConsentRecord, ConsentType, EmployerFeedback, Interview, Opportunity, SavedOpportunity, SearchMandate } from '../types/search';
import type { AppNotification, AuditAction, AuditLogEntry, CandidateDocument, DocumentCategory, DocumentReview, DocumentVersion, EmailTemplate, Message, MessageThread, NotificationType, OutboxEntry, Payment, PrivacyRequest, PrivacyRequestKind, ServiceOrder, SupportTicket, Task, TicketQueue } from '../types/platform';
import type { Article, ContentPage, IndustryDefinition, Testimonial } from '../types/content';
import { candidateProfiles, employerProfiles, recruiterProfiles, users as seedUsers } from '../data/accounts';
import { opportunities as seedOpportunities } from '../data/opportunities';
import { applications as seedApplications, savedOpportunities as seedSaved } from '../data/applications';
import { candidateDocuments, documentReviews, documentVersions } from '../data/documents';
import { candidatePresentations, employerFeedback as seedFeedback, interviews as seedInterviews, searchMandates } from '../data/mandates';
import { auditLog, consentRecords, privacyRequests, supportTickets } from '../data/governance';
import { messageThreads, messages as seedMessages, notifications as seedNotifications, outbox as seedOutbox, payments as seedPayments, serviceOrders as seedServiceOrders, tasks as seedTasks } from '../data/communications';
import { emailTemplates } from '../data/emailTemplates';
import { contentPages as seedContentPages } from '../data/contentPages';
import { articles as seedArticles } from '../data/articles';
import { industries as seedIndustries } from '../data/industries';
import { testimonials as seedTestimonials } from '../data/about';
import { newId, nextReference } from '../utils/format';
import { applicationStatusLabels } from '../utils/labels';
interface PlatformState {
  users: User[];
  candidateProfiles: CandidateProfile[];
  employerProfiles: EmployerProfile[];
  recruiterProfiles: RecruiterProfile[];
  opportunities: Opportunity[];
  mandates: SearchMandate[];
  applications: Application[];
  saved: SavedOpportunity[];
  documents: CandidateDocument[];
  documentVersions: DocumentVersion[];
  documentReviews: DocumentReview[];
  consents: ConsentRecord[];
  presentations: CandidatePresentation[];
  interviews: Interview[];
  feedback: EmployerFeedback[];
  threads: MessageThread[];
  messages: Message[];
  notifications: AppNotification[];
  outbox: OutboxEntry[];
  templates: EmailTemplate[];
  tasks: Task[];
  tickets: SupportTicket[];
  privacyRequests: PrivacyRequest[];
  auditLog: AuditLogEntry[];
  serviceOrders: ServiceOrder[];
  payments: Payment[];
  contentPages: ContentPage[];
  articles: Article[];
  industries: IndustryDefinition[];
  testimonials: Testimonial[];
}
interface Actor {
  userId: string;
  name: string;
  roleLabel: string;
}
interface PlatformActions {
  record: (action: AuditAction, actor: Actor, subjectType: string, subjectId: string, summary: string) => void;
  notify: (userId: string, type: NotificationType, title: string, body: string, linkTo?: string) => void;
  sendEmail: (templateType: NotificationType, toEmail: string, toName: string, renderedBody: string, subject: string) => void;
  registerCandidate: (input: {
    user: Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'failedSignInAttempts' | 'demoPassword'>;
    profile: Omit<CandidateProfile, 'id' | 'userId' | 'createdAt' | 'updatedAt'>;
    consents: {
      type: ConsentType;
      granted: boolean;
    }[];
    password: string;
  }) => {
    userId: string;
    profileId: string;
  };
  updateCandidateProfile: (profileId: string, patch: Partial<CandidateProfile>) => void;
  toggleSaveOpportunity: (candidateProfileId: string, opportunityId: string) => void;
  submitApplication: (input: {
    candidateProfileId: string;
    opportunityId: string;
    resumeDocumentId?: string;
    screeningAnswers: {
      questionId: string;
      answer: string;
    }[];
    compensationAlignmentConfirmed: boolean;
    locationAlignmentConfirmed: boolean;
    actor: Actor;
  }) => Application;
  withdrawApplication: (applicationId: string, actor: Actor, reason: string) => void;
  advanceApplication: (applicationId: string, status: ApplicationStatus, note: string, actor: Actor) => void;
  recordAdverseDecision: (applicationId: string, reason: string, actor: Actor) => void;
  uploadDocumentVersion: (input: {
    candidateProfileId: string;
    category: DocumentCategory;
    fileName: string;
    fileType: 'pdf' | 'doc' | 'docx';
    fileSizeBytes: number;
    actor: Actor;
  }) => void;
  requestDocument: (candidateProfileId: string, category: DocumentCategory, recruiterProfileId: string, actor: Actor) => void;
  reviewDocument: (input: {
    documentId: string;
    outcome: DocumentReview['outcome'];
    candidateVisibleFeedback: DocumentReview['candidateVisibleFeedback'];
    internalNote?: string;
    actor: Actor;
  }) => void;
  recordConsent: (input: Omit<ConsentRecord, 'id' | 'recordedAt'>, actor: Actor) => ConsentRecord;
  requestPresentationAuthorization: (input: {
    applicationId: string;
    employerNamedInRequest?: string;
    disclosureRestrictionReason?: string;
    documentIds: string[];
    actor: Actor;
  }) => void;
  respondToAuthorization: (presentationId: string, granted: boolean, actor: Actor) => void;
  markPresented: (presentationId: string, actor: Actor) => void;
  respondToInterview: (interviewId: string, slot: string, actor: Actor) => void;
  submitEmployerFeedback: (input: Omit<EmployerFeedback, 'id' | 'submittedAt'>, actor: Actor) => void;
  requestInterview: (presentationId: string, stageLabel: string, slots: string[], actor: Actor) => void;
  sendMessage: (threadId: string, body: string, actor: Actor) => void;
  markThreadRead: (threadId: string, userId: string) => void;
  markNotificationRead: (notificationId: string) => void;
  markAllNotificationsRead: (userId: string) => void;
  createTicket: (input: Omit<SupportTicket, 'id' | 'reference' | 'status' | 'submittedAt'>) => SupportTicket;
  createPrivacyRequest: (input: {
    kind: PrivacyRequestKind;
    requesterEmail: string;
    requesterUserId?: string;
    note?: string;
    actor?: Actor;
  }) => PrivacyRequest;
  createMandateInquiry: (input: Omit<SearchMandate, 'id' | 'reference' | 'stage' | 'createdAt' | 'updatedAt'>) => SearchMandate;
  setMandateStage: (mandateId: string, stage: SearchMandate['stage'], actor: Actor) => void;
  verifyEmployer: (employerProfileId: string, decision: 'verified' | 'information_requested' | 'declined', notes: string, actor: Actor) => void;
  verifyOpportunity: (opportunityId: string, actor: Actor) => void;
  setOpportunityLifecycle: (opportunityId: string, lifecycle: Opportunity['lifecycle'], actor: Actor) => void;
  updateContentBlock: (pageId: string, blockKey: string, value: string) => void;
  clearPlaceholderFlag: (pageId: string, blockKey: string) => void;
  publishContentPage: (pageId: string, summary: string, actor: Actor) => void;
  scheduleContentPage: (pageId: string, when: string) => void;
  rollbackContentPage: (pageId: string, revisionId: string, actor: Actor) => void;
  setArticleState: (slug: string, state: Article['publishState'], actor: Actor) => void;
  updateTestimonial: (id: string, patch: Partial<Testimonial>) => void;
  updateEmailTemplate: (id: string, patch: Partial<EmailTemplate>) => void;
  completeTask: (taskId: string) => void;
  createTask: (input: Omit<Task, 'id' | 'createdAt'>) => void;
  requestCareerService: (candidateProfileId: string, serviceSlug: string, serviceName: string) => ServiceOrder;
  setUserStatus: (userId: string, status: User['status'], actor: Actor) => void;
}
type PlatformContextValue = PlatformState & PlatformActions;
const PlatformContext = createContext<PlatformContextValue | null>(null);
const initialState: PlatformState = {
  users: seedUsers,
  candidateProfiles,
  employerProfiles,
  recruiterProfiles,
  opportunities: seedOpportunities,
  mandates: searchMandates,
  applications: seedApplications,
  saved: seedSaved,
  documents: candidateDocuments,
  documentVersions,
  documentReviews,
  consents: consentRecords,
  presentations: candidatePresentations,
  interviews: seedInterviews,
  feedback: seedFeedback,
  threads: messageThreads,
  messages: seedMessages,
  notifications: seedNotifications,
  outbox: seedOutbox,
  templates: emailTemplates,
  tasks: seedTasks,
  tickets: supportTickets,
  privacyRequests,
  auditLog,
  serviceOrders: seedServiceOrders,
  payments: seedPayments,
  contentPages: seedContentPages,
  articles: seedArticles,
  industries: seedIndustries,
  testimonials: seedTestimonials
};
export function PlatformProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<PlatformState>(initialState);
  const patch = useCallback((fn: (s: PlatformState) => Partial<PlatformState>) => {
    setState(s => ({
      ...s,
      ...fn(s)
    }));
  }, []);
  const record = useCallback<PlatformActions['record']>((action, actor, subjectType, subjectId, summary) => {
    patch(s => ({
      auditLog: [{
        id: newId('audit'),
        action,
        actorUserId: actor.userId,
        actorName: actor.name,
        actorRoleLabel: actor.roleLabel,
        subjectType,
        subjectId,
        summary,
        occurredAt: new Date().toISOString()
      }, ...s.auditLog]
    }));
  }, [patch]);
  const sendEmail = useCallback<PlatformActions['sendEmail']>((templateType, toEmail, toName, renderedBody, subject) => {
    const template = emailTemplates.find(t => t.type === templateType);
    const marketing = template?.category === 'marketing';
    patch(s => ({
      outbox: [{
        id: newId('out'),
        templateId: template?.id ?? 'tpl-unknown',
        type: templateType,
        toEmail,
        toName,
        subject,
        renderedBody,
        sentAt: new Date().toISOString(),
        deliveryState: marketing ? 'queued' : 'delivered',
        containsAttachment: false
      }, ...s.outbox]
    }));
  }, [patch]);
  const notify = useCallback<PlatformActions['notify']>((userId, type, title, body, linkTo) => {
    patch(s => ({
      notifications: [{
        id: newId('note'),
        userId,
        type,
        title,
        body,
        linkTo,
        createdAt: new Date().toISOString()
      }, ...s.notifications]
    }));
    const user = state.users.find(u => u.id === userId);
    if (user) sendEmail(type, user.email, user.preferredName ?? user.fullLegalName, body, title);
  }, [patch, sendEmail, state.users]);
  const registerCandidate = useCallback<PlatformActions['registerCandidate']>(({
    user,
    profile,
    consents,
    password
  }) => {
    const userId = newId('user');
    const profileId = newId('cand');
    const now = new Date().toISOString();
    patch(s => ({
      users: [...s.users, {
        ...user,
        id: userId,
        createdAt: now,
        updatedAt: now,
        failedSignInAttempts: 0,
        demoPassword: password
      }],
      candidateProfiles: [...s.candidateProfiles, {
        ...profile,
        id: profileId,
        userId,
        createdAt: now,
        updatedAt: now
      }],
      consents: [...s.consents, ...consents.map(c => ({
        id: newId('consent'),
        candidateProfileId: profileId,
        type: c.type,
        granted: c.granted,
        recordedAt: now,
        recordedVia: 'registration' as const,
        ipNote: 'Recorded at registration. In production the request origin is logged server-side.'
      }))],
      auditLog: [{
        id: newId('audit'),
        action: 'user.created',
        actorUserId: userId,
        actorName: user.fullLegalName,
        actorRoleLabel: 'Candidate',
        subjectType: 'User',
        subjectId: userId,
        summary: 'Candidate account created through registration.',
        occurredAt: now
      }, ...s.auditLog],
      notifications: [{
        id: newId('note'),
        userId,
        type: 'registration_confirmation',
        title: 'Registration confirmed',
        body: 'Your account has been created. Registration places you in our research network; it is not an application and does not guarantee consideration.',
        linkTo: '/portal/candidate',
        createdAt: now
      }, ...s.notifications],
      outbox: [{
        id: newId('out'),
        templateId: 'tpl-registration-confirmation',
        type: 'registration_confirmation' as const,
        toEmail: user.email,
        toName: user.preferredName ?? user.fullLegalName,
        subject: 'Your Velnoris Executive Network registration',
        renderedBody: `Dear ${user.preferredName ?? user.fullLegalName},\n\nThank you for registering with Velnoris Executive Network. Your account has been created.\n\nRegistration places you in our research network; it is not an application and does not guarantee consideration for any particular mandate.\n\nNothing about you is shared with any employer without your specific authorization for that mandate.\n\nVelnoris Executive Network`,
        sentAt: now,
        deliveryState: 'delivered' as const,
        containsAttachment: false as const
      }, ...s.outbox]
    }));
    return {
      userId,
      profileId
    };
  }, [patch]);
  const updateCandidateProfile = useCallback<PlatformActions['updateCandidateProfile']>((profileId, profilePatch) => {
    patch(s => ({
      candidateProfiles: s.candidateProfiles.map(p => p.id === profileId ? {
        ...p,
        ...profilePatch,
        updatedAt: new Date().toISOString()
      } : p)
    }));
  }, [patch]);
  const toggleSaveOpportunity = useCallback<PlatformActions['toggleSaveOpportunity']>((candidateProfileId, opportunityId) => {
    patch(s => {
      const existing = s.saved.find(x => x.candidateProfileId === candidateProfileId && x.opportunityId === opportunityId);
      return existing ? {
        saved: s.saved.filter(x => x.id !== existing.id)
      } : {
        saved: [...s.saved, {
          id: newId('saved'),
          candidateProfileId,
          opportunityId,
          savedAt: new Date().toISOString()
        }]
      };
    });
  }, [patch]);
  const submitApplication = useCallback<PlatformActions['submitApplication']>(({
    candidateProfileId,
    opportunityId,
    resumeDocumentId,
    screeningAnswers,
    compensationAlignmentConfirmed,
    locationAlignmentConfirmed,
    actor
  }) => {
    const now = new Date().toISOString();
    const application: Application = {
      id: newId('app'),
      reference: nextReference('APP'),
      opportunityId,
      candidateProfileId,
      status: 'submitted',
      submittedAt: now,
      resumeDocumentId,
      screeningAnswers,
      compensationAlignmentConfirmed,
      locationAlignmentConfirmed,
      internalReviewConsentAt: now,
      events: [{
        id: newId('ev'),
        status: 'submitted',
        occurredAt: now,
        actorUserId: actor.userId,
        actorRoleLabel: actor.roleLabel,
        note: 'Application submitted with screening responses and consent to internal review.',
        candidateVisible: true
      }],
      createdAt: now,
      updatedAt: now
    };
    const opportunity = state.opportunities.find(o => o.id === opportunityId);
    patch(s => ({
      applications: [application, ...s.applications],
      consents: [...s.consents, {
        id: newId('consent'),
        candidateProfileId,
        type: 'internal_review' as const,
        granted: true,
        opportunityId,
        recordedAt: now,
        recordedVia: 'application' as const,
        ipNote: `Recorded during application submission for ${application.reference}.`
      }]
    }));
    record('application.status_changed', actor, 'Application', application.id, `Application ${application.reference} submitted.`);
    notify(actor.userId, 'application_received', `Application received — ${application.reference}`, `We have received your application for ${opportunity?.title ?? 'this opportunity'}. Your reference is ${application.reference}.`, '/portal/candidate/applications');
    return application;
  }, [notify, patch, record, state.opportunities]);
  const appendEvent = useCallback((applicationId: string, status: ApplicationStatus, note: string, actor: Actor, extra?: Partial<Application>) => {
    const now = new Date().toISOString();
    patch(s => ({
      applications: s.applications.map(a => a.id === applicationId ? {
        ...a,
        ...extra,
        status,
        updatedAt: now,
        events: [...a.events, {
          id: newId('ev'),
          status,
          occurredAt: now,
          actorUserId: actor.userId,
          actorRoleLabel: actor.roleLabel,
          note,
          candidateVisible: true
        }]
      } : a)
    }));
  }, [patch]);
  const withdrawApplication = useCallback<PlatformActions['withdrawApplication']>((applicationId, actor, reason) => {
    appendEvent(applicationId, 'withdrawn', `Withdrawn by the candidate. ${reason}`.trim(), actor);
    const app = state.applications.find(a => a.id === applicationId);
    record('application.status_changed', actor, 'Application', applicationId, `Application ${app?.reference ?? applicationId} withdrawn by candidate.`);
    patch(s => ({
      presentations: s.presentations.map(p => p.applicationId === applicationId && p.status === 'authorization_requested' ? {
        ...p,
        status: 'withdrawn'
      } : p)
    }));
  }, [appendEvent, patch, record, state.applications]);
  const advanceApplication = useCallback<PlatformActions['advanceApplication']>((applicationId, status, note, actor) => {
    appendEvent(applicationId, status, note, actor);
    const app = state.applications.find(a => a.id === applicationId);
    record('application.status_changed', actor, 'Application', applicationId, `Status changed to ${applicationStatusLabels[status]} for ${app?.reference ?? applicationId}.`);
    const profile = state.candidateProfiles.find(p => p.id === app?.candidateProfileId);
    const user = state.users.find(u => u.id === profile?.userId);
    if (user) {
      notify(user.id, 'status_update', `Update on your application — ${app?.reference ?? ''}`, `The status of your application has changed to: ${applicationStatusLabels[status]}. ${note}`, '/portal/candidate/applications');
    }
  }, [appendEvent, notify, record, state.applications, state.candidateProfiles, state.users]);
  const recordAdverseDecision = useCallback<PlatformActions['recordAdverseDecision']>((applicationId, reason, actor) => {
    appendEvent(applicationId, 'not_selected', `Not selected. Reviewer reasoning: ${reason}`, actor, {
      adverseDecision: {
        reviewerUserId: actor.userId,
        reviewerName: actor.name,
        reason,
        recordedAt: new Date().toISOString()
      }
    });
    record('application.adverse_decision_recorded', actor, 'Application', applicationId, `Adverse decision recorded by a named reviewer with a written reason.`);
  }, [appendEvent, record]);
  const uploadDocumentVersion = useCallback<PlatformActions['uploadDocumentVersion']>(({
    candidateProfileId,
    category,
    fileName,
    fileType,
    fileSizeBytes,
    actor
  }) => {
    const now = new Date().toISOString();
    patch(s => {
      const existing = s.documents.find(d => d.candidateProfileId === candidateProfileId && d.category === category);
      const documentId = existing?.id ?? newId('doc');
      const versionNumber = s.documentVersions.filter(v => v.documentId === documentId).reduce((m, v) => Math.max(m, v.versionNumber), 0) + 1;
      const versionId = newId('ver');
      const version: DocumentVersion = {
        id: versionId,
        documentId,
        versionNumber,
        fileName,
        fileType,
        fileSizeBytes,
        uploadedAt: now,
        uploadedByUserId: actor.userId,
        scanStatus: 'clean',
        storageKey: `private/${candidateProfileId}/${documentId}/v${versionNumber}`
      };
      return {
        documentVersions: [...s.documentVersions, version],
        documents: existing ? s.documents.map(d => d.id === documentId ? {
          ...d,
          currentVersionId: versionId,
          reviewStatus: versionNumber > 1 ? 'updated_version_received' : 'submitted',
          updatedAt: now
        } : d) : [...s.documents, {
          id: documentId,
          candidateProfileId,
          category,
          reviewStatus: 'submitted' as const,
          currentVersionId: versionId,
          createdAt: now,
          updatedAt: now
        }]
      };
    });
    record('document.uploaded', actor, 'DocumentVersion', fileName, `${fileName} uploaded as a new version. Prior versions retained.`);
  }, [patch, record]);
  const requestDocument = useCallback<PlatformActions['requestDocument']>((candidateProfileId, category, recruiterProfileId, actor) => {
    const now = new Date().toISOString();
    patch(s => {
      const existing = s.documents.find(d => d.candidateProfileId === candidateProfileId && d.category === category);
      return existing ? {
        documents: s.documents.map(d => d.id === existing.id ? {
          ...d,
          requestedByRecruiterId: recruiterProfileId,
          requestedAt: now,
          updatedAt: now
        } : d)
      } : {
        documents: [...s.documents, {
          id: newId('doc'),
          candidateProfileId,
          category,
          reviewStatus: 'not_submitted' as const,
          currentVersionId: '',
          requestedByRecruiterId: recruiterProfileId,
          requestedAt: now,
          createdAt: now,
          updatedAt: now
        }]
      };
    });
    const profile = state.candidateProfiles.find(p => p.id === candidateProfileId);
    const user = state.users.find(u => u.id === profile?.userId);
    if (user) {
      notify(user.id, 'document_received', 'A document has been requested', 'A consultant has requested a document relevant to a live mandate. It is optional, and the reason is recorded in your document centre.', '/portal/candidate/documents');
    }
  }, [notify, patch, state.candidateProfiles, state.users]);
  const reviewDocument = useCallback<PlatformActions['reviewDocument']>(({
    documentId,
    outcome,
    candidateVisibleFeedback,
    internalNote,
    actor
  }) => {
    const now = new Date().toISOString();
    const doc = state.documents.find(d => d.id === documentId);
    patch(s => ({
      documentReviews: [...s.documentReviews, {
        id: newId('rev'),
        documentId,
        documentVersionId: doc?.currentVersionId ?? '',
        reviewerUserId: actor.userId,
        reviewerName: actor.name,
        outcome,
        candidateVisibleFeedback,
        internalNote,
        reviewedAt: now
      }],
      documents: s.documents.map(d => d.id === documentId ? {
        ...d,
        reviewStatus: outcome,
        updatedAt: now
      } : d)
    }));
    record('document.reviewed', actor, 'DocumentReview', documentId, `Review completed with outcome ${outcome}.`);
    const profile = state.candidateProfiles.find(p => p.id === doc?.candidateProfileId);
    const user = state.users.find(u => u.id === profile?.userId);
    if (user) {
      const revision = outcome === 'revision_requested';
      notify(user.id, revision ? 'revision_requested' : 'document_review_completed', revision ? 'Document review completed — revision requested' : 'Document review completed', revision ? `A consultant has requested a revision with ${candidateVisibleFeedback.length} specific items. No paid service is required to act on it.` : 'A consultant has completed the review of your document.', '/portal/candidate/documents');
    }
  }, [notify, patch, record, state.candidateProfiles, state.documents, state.users]);
  const recordConsent = useCallback<PlatformActions['recordConsent']>((input, actor) => {
    const consent: ConsentRecord = {
      ...input,
      id: newId('consent'),
      recordedAt: new Date().toISOString()
    };
    patch(s => ({
      consents: [...s.consents, consent]
    }));
    record('consent.recorded', actor, 'ConsentRecord', consent.id, `${input.type} recorded as ${input.granted ? 'granted' : 'declined'}.`);
    return consent;
  }, [patch, record]);
  const requestPresentationAuthorization = useCallback<PlatformActions['requestPresentationAuthorization']>(({
    applicationId,
    employerNamedInRequest,
    disclosureRestrictionReason,
    documentIds,
    actor
  }) => {
    const app = state.applications.find(a => a.id === applicationId);
    if (!app) return;
    const opportunity = state.opportunities.find(o => o.id === app.opportunityId);
    const mandate = state.mandates.find(m => m.opportunityId === app.opportunityId);
    const now = new Date().toISOString();
    const presentation: CandidatePresentation = {
      id: newId('pres'),
      mandateId: mandate?.id ?? '',
      opportunityId: app.opportunityId,
      candidateProfileId: app.candidateProfileId,
      applicationId,
      employerProfileId: mandate?.employerProfileId ?? '',
      status: 'authorization_requested',
      employerNamedInRequest,
      disclosureRestrictionReason,
      requestedAt: now,
      documentIdsShared: documentIds
    };
    patch(s => ({
      presentations: [presentation, ...s.presentations]
    }));
    appendEvent(applicationId, 'candidate_consent_required', employerNamedInRequest ? `Presentation authorization requested, naming the client as ${employerNamedInRequest}.` : 'Presentation authorization requested. Client identity is temporarily restricted; the reason is stated in the request.', actor);
    record('presentation.authorization_requested', actor, 'CandidatePresentation', presentation.id, `Authorization requested for ${opportunity?.referenceNumber ?? app.opportunityId}.`);
    const profile = state.candidateProfiles.find(p => p.id === app.candidateProfileId);
    const user = state.users.find(u => u.id === profile?.userId);
    if (user) {
      notify(user.id, 'consent_request', 'Presentation authorization required', `Your authorization is required before your profile can be presented for ${opportunity?.referenceNumber ?? 'this mandate'}. You may decline; declining will not affect your candidacy.`, '/portal/candidate/consent');
    }
  }, [appendEvent, notify, patch, record, state.applications, state.candidateProfiles, state.mandates, state.opportunities, state.users]);
  const respondToAuthorization = useCallback<PlatformActions['respondToAuthorization']>((presentationId, granted, actor) => {
    const pres = state.presentations.find(p => p.id === presentationId);
    if (!pres) return;
    const now = new Date().toISOString();
    const consent = recordConsent({
      candidateProfileId: pres.candidateProfileId,
      type: 'mandate_specific_presentation',
      granted,
      mandateId: pres.mandateId,
      opportunityId: pres.opportunityId,
      employerDisclosedAs: pres.employerNamedInRequest,
      disclosureRestrictionReason: pres.disclosureRestrictionReason,
      recordedVia: 'authorization_request',
      ipNote: granted ? 'Candidate authorized presentation.' : 'Candidate declined. Application remains open; candidacy unaffected.'
    }, actor);
    patch(s => ({
      presentations: s.presentations.map(p => p.id === presentationId ? {
        ...p,
        status: granted ? 'authorized' : 'declined_by_candidate',
        authorizationConsentId: consent.id,
        respondedAt: now
      } : p)
    }));
    appendEvent(pres.applicationId, granted ? 'client_authorized_submission_pending' : 'on_hold', granted ? 'Candidate authorized presentation. Awaiting submission to the client.' : 'Candidate declined presentation authorization for this mandate. The application remains open and the candidacy is unaffected.', actor);
    record(granted ? 'presentation.authorized' : 'presentation.declined', actor, 'CandidatePresentation', presentationId, granted ? 'Candidate authorized presentation to the client.' : 'Candidate declined presentation authorization.');
  }, [appendEvent, patch, record, recordConsent, state.presentations]);
  const markPresented = useCallback<PlatformActions['markPresented']>((presentationId, actor) => {
    const pres = state.presentations.find(p => p.id === presentationId);
    if (!pres || pres.status !== 'authorized') return;
    const now = new Date().toISOString();
    patch(s => ({
      presentations: s.presentations.map(p => p.id === presentationId ? {
        ...p,
        status: 'presented',
        presentedAt: now
      } : p)
    }));
    appendEvent(pres.applicationId, 'presented_to_client', 'Profile and written assessment presented to the client following recorded authorization.', actor);
    record('presentation.sent_to_client', actor, 'CandidatePresentation', presentationId, 'Presented to client. Authorization verified before release.');
  }, [appendEvent, patch, record, state.presentations]);
  const respondToInterview = useCallback<PlatformActions['respondToInterview']>((interviewId, slot, actor) => {
    patch(s => ({
      interviews: s.interviews.map(i => i.id === interviewId ? {
        ...i,
        status: 'scheduled',
        confirmedSlot: slot
      } : i)
    }));
    record('application.status_changed', actor, 'Interview', interviewId, 'Candidate confirmed interview availability.');
  }, [patch, record]);
  const requestInterview = useCallback<PlatformActions['requestInterview']>((presentationId, stageLabel, slots, actor) => {
    const pres = state.presentations.find(p => p.id === presentationId);
    if (!pres) return;
    const interview: Interview = {
      id: newId('int'),
      presentationId,
      mandateId: pres.mandateId,
      candidateProfileId: pres.candidateProfileId,
      employerProfileId: pres.employerProfileId,
      stageLabel,
      format: 'video',
      status: 'availability_requested',
      proposedSlots: slots,
      panel: [],
      createdAt: new Date().toISOString()
    };
    patch(s => ({
      interviews: [interview, ...s.interviews]
    }));
    const profile = state.candidateProfiles.find(p => p.id === pres.candidateProfileId);
    const user = state.users.find(u => u.id === profile?.userId);
    if (user) {
      notify(user.id, 'interview_request', 'Interview requested', `${stageLabel}. Proposed times are available in your portal. Reasonable accommodations are available on request.`, '/portal/candidate/interviews');
    }
    record('application.status_changed', actor, 'Interview', interview.id, `Interview requested: ${stageLabel}.`);
  }, [notify, patch, record, state.candidateProfiles, state.presentations, state.users]);
  const submitEmployerFeedback = useCallback<PlatformActions['submitEmployerFeedback']>((input, actor) => {
    patch(s => ({
      feedback: [{
        ...input,
        id: newId('fb'),
        submittedAt: new Date().toISOString()
      }, ...s.feedback]
    }));
    record('application.status_changed', actor, 'EmployerFeedback', input.presentationId, `Structured employer feedback submitted: ${input.recommendation}.`);
  }, [patch, record]);
  const sendMessage = useCallback<PlatformActions['sendMessage']>((threadId, body, actor) => {
    const now = new Date().toISOString();
    patch(s => ({
      messages: [...s.messages, {
        id: newId('msg'),
        threadId,
        authorUserId: actor.userId,
        authorName: actor.name,
        authorRoleLabel: actor.roleLabel,
        body,
        sentAt: now,
        readByUserIds: [actor.userId]
      }],
      threads: s.threads.map(t => t.id === threadId ? {
        ...t,
        lastMessageAt: now
      } : t)
    }));
  }, [patch]);
  const markThreadRead = useCallback<PlatformActions['markThreadRead']>((threadId, userId) => {
    patch(s => ({
      messages: s.messages.map(m => m.threadId === threadId && !m.readByUserIds.includes(userId) ? {
        ...m,
        readByUserIds: [...m.readByUserIds, userId]
      } : m)
    }));
  }, [patch]);
  const markNotificationRead = useCallback<PlatformActions['markNotificationRead']>(notificationId => {
    patch(s => ({
      notifications: s.notifications.map(n => n.id === notificationId && !n.readAt ? {
        ...n,
        readAt: new Date().toISOString()
      } : n)
    }));
  }, [patch]);
  const markAllNotificationsRead = useCallback<PlatformActions['markAllNotificationsRead']>(userId => {
    const now = new Date().toISOString();
    patch(s => ({
      notifications: s.notifications.map(n => n.userId === userId && !n.readAt ? {
        ...n,
        readAt: now
      } : n)
    }));
  }, [patch]);
  const createTicket = useCallback<PlatformActions['createTicket']>(input => {
    const ticket: SupportTicket = {
      ...input,
      id: newId('tick'),
      reference: nextReference('HLP'),
      status: 'new',
      submittedAt: new Date().toISOString()
    };
    patch(s => ({
      tickets: [ticket, ...s.tickets]
    }));
    return ticket;
  }, [patch]);
  const createPrivacyRequest = useCallback<PlatformActions['createPrivacyRequest']>(({
    kind,
    requesterEmail,
    requesterUserId,
    note,
    actor
  }) => {
    const now = new Date();
    const request: PrivacyRequest = {
      id: newId('pr'),
      reference: nextReference('PRQ'),
      kind,
      requesterUserId,
      requesterEmail,
      status: 'received',
      submittedAt: now.toISOString(),
      dueAt: new Date(now.getTime() + 30 * 86_400_000).toISOString(),
      note
    };
    patch(s => ({
      privacyRequests: [request, ...s.privacyRequests]
    }));
    if (actor) {
      record('privacy.request_received', actor, 'PrivacyRequest', request.id, `${kind} request received. Reference ${request.reference}.`);
    }
    if (requesterUserId) {
      notify(requesterUserId, 'privacy_request_confirmation', `Privacy request received — ${request.reference}`, 'We have received your request and will confirm the outcome in writing.', '/portal/candidate/privacy');
    }
    return request;
  }, [notify, patch, record]);
  const createMandateInquiry = useCallback<PlatformActions['createMandateInquiry']>(input => {
    const now = new Date().toISOString();
    const mandate: SearchMandate = {
      ...input,
      id: newId('mandate'),
      reference: nextReference('MND'),
      stage: 'inquiry_received',
      createdAt: now,
      updatedAt: now
    };
    patch(s => ({
      mandates: [mandate, ...s.mandates]
    }));
    return mandate;
  }, [patch]);
  const setMandateStage = useCallback<PlatformActions['setMandateStage']>((mandateId, stage, actor) => {
    patch(s => ({
      mandates: s.mandates.map(m => m.id === mandateId ? {
        ...m,
        stage,
        updatedAt: new Date().toISOString()
      } : m)
    }));
    record('application.status_changed', actor, 'SearchMandate', mandateId, `Mandate stage set to ${stage}.`);
  }, [patch, record]);
  const verifyEmployer = useCallback<PlatformActions['verifyEmployer']>((employerProfileId, decision, notes, actor) => {
    const now = new Date().toISOString();
    patch(s => ({
      employerProfiles: s.employerProfiles.map(e => e.id === employerProfileId ? {
        ...e,
        verificationStatus: decision,
        verificationNotes: notes,
        verifiedByUserId: decision === 'verified' ? actor.userId : undefined,
        verifiedAt: decision === 'verified' ? now : undefined,
        updatedAt: now
      } : e),
      users: s.users.map(u => {
        const emp = s.employerProfiles.find(e => e.id === employerProfileId);
        return emp && u.id === emp.userId ? {
          ...u,
          status: decision === 'verified' ? 'active' : 'pending_verification',
          updatedAt: now
        } : u;
      })
    }));
    record('employer.verified', actor, 'EmployerProfile', employerProfileId, `Verification decision: ${decision}. ${notes}`);
  }, [patch, record]);
  const verifyOpportunity = useCallback<PlatformActions['verifyOpportunity']>((opportunityId, actor) => {
    const now = new Date().toISOString();
    patch(s => ({
      opportunities: s.opportunities.map(o => o.id === opportunityId ? {
        ...o,
        internal: {
          ...o.internal,
          verifiedByUserId: actor.userId,
          verificationDate: now
        },
        updatedAt: now
      } : o)
    }));
    record('opportunity.verified', actor, 'Opportunity', opportunityId, 'Verification evidence reviewed and accepted.');
  }, [patch, record]);
  const setOpportunityLifecycle = useCallback<PlatformActions['setOpportunityLifecycle']>((opportunityId, lifecycle, actor) => {
    patch(s => ({
      opportunities: s.opportunities.map(o => o.id === opportunityId ? {
        ...o,
        lifecycle,
        updatedAt: new Date().toISOString()
      } : o)
    }));
    record(lifecycle === 'open' ? 'opportunity.published' : 'opportunity.verified', actor, 'Opportunity', opportunityId, `Lifecycle set to ${lifecycle}.`);
  }, [patch, record]);
  const updateContentBlock = useCallback<PlatformActions['updateContentBlock']>((pageId, blockKey, value) => {
    patch(s => ({
      contentPages: s.contentPages.map(p => p.id === pageId ? {
        ...p,
        publishState: p.publishState === 'published' ? 'draft' : p.publishState,
        blocks: p.blocks.map(b => b.key === blockKey ? {
          ...b,
          value
        } : b),
        updatedAt: new Date().toISOString()
      } : p)
    }));
  }, [patch]);
  const clearPlaceholderFlag = useCallback<PlatformActions['clearPlaceholderFlag']>((pageId, blockKey) => {
    patch(s => ({
      contentPages: s.contentPages.map(p => p.id === pageId ? {
        ...p,
        blocks: p.blocks.map(b => b.key === blockKey ? {
          ...b,
          isUnverifiedPlaceholder: false,
          placeholderNote: undefined
        } : b),
        updatedAt: new Date().toISOString()
      } : p)
    }));
  }, [patch]);
  const publishContentPage = useCallback<PlatformActions['publishContentPage']>((pageId, summary, actor) => {
    const now = new Date().toISOString();
    patch(s => ({
      contentPages: s.contentPages.map(p => {
        if (p.id !== pageId) return p;
        const nextRevision = p.revisions.reduce((m, r) => Math.max(m, r.revisionNumber), 0) + 1;
        return {
          ...p,
          publishState: 'published',
          scheduledFor: undefined,
          updatedAt: now,
          updatedByName: actor.name,
          revisions: [{
            id: newId('rev'),
            revisionNumber: nextRevision,
            savedAt: now,
            savedByName: actor.name,
            summary,
            blocks: p.blocks.reduce<Record<string, string>>((acc, b) => {
              acc[b.key] = b.value;
              return acc;
            }, {})
          }, ...p.revisions]
        };
      })
    }));
    record('content.published', actor, 'ContentPage', pageId, summary);
  }, [patch, record]);
  const scheduleContentPage = useCallback<PlatformActions['scheduleContentPage']>((pageId, when) => {
    patch(s => ({
      contentPages: s.contentPages.map(p => p.id === pageId ? {
        ...p,
        publishState: 'scheduled',
        scheduledFor: when
      } : p)
    }));
  }, [patch]);
  const rollbackContentPage = useCallback<PlatformActions['rollbackContentPage']>((pageId, revisionId, actor) => {
    patch(s => ({
      contentPages: s.contentPages.map(p => {
        if (p.id !== pageId) return p;
        const revision = p.revisions.find(r => r.id === revisionId);
        if (!revision || !Object.keys(revision.blocks).length) return p;
        return {
          ...p,
          blocks: p.blocks.map(b => revision.blocks[b.key] !== undefined ? {
            ...b,
            value: revision.blocks[b.key]
          } : b),
          publishState: 'draft',
          updatedAt: new Date().toISOString(),
          updatedByName: actor.name
        };
      })
    }));
    record('content.published', actor, 'ContentPage', pageId, `Rolled back to revision ${revisionId}.`);
  }, [patch, record]);
  const setArticleState = useCallback<PlatformActions['setArticleState']>((slug, publishState, actor) => {
    patch(s => ({
      articles: s.articles.map(a => a.slug === slug ? {
        ...a,
        publishState
      } : a)
    }));
    record('content.published', actor, 'Article', slug, `Article publish state set to ${publishState}.`);
  }, [patch, record]);
  const updateTestimonial = useCallback<PlatformActions['updateTestimonial']>((id, testimonialPatch) => {
    patch(s => ({
      testimonials: s.testimonials.map(t => t.id === id ? {
        ...t,
        ...testimonialPatch
      } : t)
    }));
  }, [patch]);
  const updateEmailTemplate = useCallback<PlatformActions['updateEmailTemplate']>((id, templatePatch) => {
    patch(s => ({
      templates: s.templates.map(t => t.id === id ? {
        ...t,
        ...templatePatch,
        updatedAt: new Date().toISOString()
      } : t)
    }));
  }, [patch]);
  const completeTask = useCallback<PlatformActions['completeTask']>(taskId => {
    patch(s => ({
      tasks: s.tasks.map(t => t.id === taskId ? {
        ...t,
        completedAt: new Date().toISOString()
      } : t)
    }));
  }, [patch]);
  const createTask = useCallback<PlatformActions['createTask']>(input => {
    patch(s => ({
      tasks: [{
        ...input,
        id: newId('task'),
        createdAt: new Date().toISOString()
      }, ...s.tasks]
    }));
  }, [patch]);
  const requestCareerService = useCallback<PlatformActions['requestCareerService']>((candidateProfileId, serviceSlug, serviceName) => {
    const order: ServiceOrder = {
      id: newId('so'),
      reference: nextReference('ECS'),
      candidateProfileId,
      serviceSlug,
      serviceName,
      status: 'consultation_requested',
      revisionsIncluded: 0,
      revisionsUsed: 0,
      consultationRequestedAt: new Date().toISOString(),
      notes: 'Consultation requested. No charge has been raised and no scope agreed.'
    };
    patch(s => ({
      serviceOrders: [order, ...s.serviceOrders]
    }));
    return order;
  }, [patch]);
  const setUserStatus = useCallback<PlatformActions['setUserStatus']>((userId, status, actor) => {
    patch(s => ({
      users: s.users.map(u => u.id === userId ? {
        ...u,
        status,
        updatedAt: new Date().toISOString()
      } : u)
    }));
    record('user.role_changed', actor, 'User', userId, `Account status set to ${status}.`);
  }, [patch, record]);
  const value = useMemo<PlatformContextValue>(() => ({
    ...state,
    record,
    notify,
    sendEmail,
    registerCandidate,
    updateCandidateProfile,
    toggleSaveOpportunity,
    submitApplication,
    withdrawApplication,
    advanceApplication,
    recordAdverseDecision,
    uploadDocumentVersion,
    requestDocument,
    reviewDocument,
    recordConsent,
    requestPresentationAuthorization,
    respondToAuthorization,
    markPresented,
    respondToInterview,
    submitEmployerFeedback,
    requestInterview,
    sendMessage,
    markThreadRead,
    markNotificationRead,
    markAllNotificationsRead,
    createTicket,
    createPrivacyRequest,
    createMandateInquiry,
    setMandateStage,
    verifyEmployer,
    verifyOpportunity,
    setOpportunityLifecycle,
    updateContentBlock,
    clearPlaceholderFlag,
    publishContentPage,
    scheduleContentPage,
    rollbackContentPage,
    setArticleState,
    updateTestimonial,
    updateEmailTemplate,
    completeTask,
    createTask,
    requestCareerService,
    setUserStatus
  }), [state, record, notify, sendEmail, registerCandidate, updateCandidateProfile, toggleSaveOpportunity, submitApplication, withdrawApplication, advanceApplication, recordAdverseDecision, uploadDocumentVersion, requestDocument, reviewDocument, recordConsent, requestPresentationAuthorization, respondToAuthorization, markPresented, respondToInterview, submitEmployerFeedback, requestInterview, sendMessage, markThreadRead, markNotificationRead, markAllNotificationsRead, createTicket, createPrivacyRequest, createMandateInquiry, setMandateStage, verifyEmployer, verifyOpportunity, setOpportunityLifecycle, updateContentBlock, clearPlaceholderFlag, publishContentPage, scheduleContentPage, rollbackContentPage, setArticleState, updateTestimonial, updateEmailTemplate, completeTask, createTask, requestCareerService, setUserStatus]);
  return <PlatformContext.Provider value={value} data-id="element-561" data-mp="3vjn1u14deu2f">{children}</PlatformContext.Provider>;
}
export function usePlatform(): PlatformContextValue {
  const ctx = useContext(PlatformContext);
  if (!ctx) throw new Error('usePlatform must be used within a PlatformProvider');
  return ctx;
}